import { Icon } from "@circle-react-shared/Icon";
import { LabelMd } from "@circle-react-shared/uikit/Typography/components/Label/LabelMd";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useBackendProps } from "@circle-react/providers/AppContext";
import { Switch } from "@circleco/compass/components/Switch";
import { truncate } from "lodash";
import type { MouseEventHandler } from "react";

import { t } from "@/i18n-js/instance";
import { useMasqueradeContext } from "@/react/contexts/masqueradeContext";
import { useIsLoadingAppData } from "@/react/hooks/useIsLoadingAppData";

// Compact is the mobile layout; kept as data so the component has one
// branch on the prop instead of one per element.
const bannerStyles = {
  default: {
    root: "h-12 px-6",
    toggle: "",
    toggleLabelClass: "text-feature-dark whitespace-nowrap",
    contentWrap: "",
    titleWrap: "",
    titleColor: "text-feature-dark",
    nameButton: "",
    nameLabelClass: "",
    nameColor: "text-feature-dark",
    icon: "text-feature-dark ml-[3px]",
    exit: "",
    exitColor: "text-feature-dark",
  },
  compact: {
    // Banner sits inside the themed MobileShell.Header, which remaps
    // --color-text-primary/--color-border-primary onto community header
    // colors. Re-pin them to the theme defaults (themes/light.css, dark.css)
    // since our bg-tertiary background isn't remapped.
    root: "bg-tertiary dark:bg-tertiary min-h-12 flex-wrap gap-x-3 gap-y-1 px-4 py-1 [--color-text-primary:var(--color-grey-900)] dark:[--color-text-primary:var(--color-grey-50)] dark:[--color-border-primary:var(--color-grey-700)]",
    toggle: "order-last w-full empty:hidden",
    toggleLabelClass: "text-primary",
    contentWrap: "min-w-0 flex-1",
    titleWrap: "shrink-0 py-2.5",
    titleColor: "text-primary",
    nameButton: "min-w-0 py-2.5",
    nameLabelClass: "min-w-0 truncate lg:dark:border-primary",
    nameColor: "text-secondary",
    icon: "text-primary",
    exit: "py-2.5",
    exitColor: "text-secondary",
  },
} as const;

interface MasqueradeBannerProps {
  /**
   * Narrow-viewport layout for mobile
   */
  isCompact?: boolean;
}

export const LegacyMasqueradeBanner = ({
  isCompact = false,
}: MasqueradeBannerProps) => {
  const { currentCommunityMember, isViewOnlyMasquerading, masqueradingEntity } =
    usePunditUserContext();
  const { masquerade_referrer_url: masqueradeReferrerUrl } = useBackendProps();
  const isLoadingAppData = useIsLoadingAppData();
  const isMasquerading = !!masqueradeReferrerUrl && !isLoadingAppData;

  const {
    isBannerVisible,
    isCircleAiAllowed,
    shouldShowCircleAiToggle,
    setIsCircleAiAllowed,
    openViewMasqueradingSearch,
    openEditMasqueradingSearch,
    hideBanner,
  } = useMasqueradeContext();

  const handleClose = () => {
    hideBanner();
    if (masqueradeReferrerUrl) {
      window.location.href = masqueradeReferrerUrl;
    }
  };

  if (!isMasquerading && !isBannerVisible) {
    return null;
  }

  const styles = bannerStyles[isCompact ? "compact" : "default"];

  let title: string;
  let ariaLabel: string;
  let name: string;
  let onClick: MouseEventHandler<HTMLButtonElement> | undefined;

  // If banner is visible, the possible option is we are in view only mode before
  // selecting an option
  if (isBannerVisible) {
    title = t("view_only_masquerading.banner.title_not_selected");
    ariaLabel = t("view_only_masquerading.banner.title_selected");
    name = t("view_only_masquerading.banner.placeholder");
    onClick = openViewMasqueradingSearch;
  } else if (isViewOnlyMasquerading) {
    // we are masquerading in view only mode
    title = t("view_only_masquerading.banner.title_selected");
    ariaLabel = t("view_only_masquerading.banner.title_selected");
    name = truncate(masqueradingEntity?.name || "", { length: 50 });
    onClick = openViewMasqueradingSearch;
  } else {
    // we are masquerading in edit mode
    title = t("masquerade.banner.title");
    ariaLabel = t("masquerade.banner.title");
    name = truncate(currentCommunityMember?.name || "", { length: 50 });
    onClick = openEditMasqueradingSearch;
  }

  return (
    <div
      className={classNames(
        "flex items-center justify-between bg-gray-900 dark:bg-gray-700",
        styles.root,
      )}
    >
      <div
        className={classNames(
          "shrink-0 [&_[role=switch]]:!border-light [&_[role=switch][data-checked]]:!border-transparent [&_[role=switch][data-checked]]:!bg-toggle-checked [&_[role=switch][data-checked]:hover]:!bg-toggle-checked",
          styles.toggle,
        )}
      >
        {shouldShowCircleAiToggle && (
          <Switch
            label={t("masquerade.banner.allow_circle_ai")}
            labelProps={{
              className: styles.toggleLabelClass,
            }}
            checked={isCircleAiAllowed}
            onCheckedChange={setIsCircleAiAllowed}
            data-testid="masquerade-allow-circle-ai"
          />
        )}
      </div>
      <div
        className={classNames("flex", styles.contentWrap)}
        data-testid="masquerade-banner"
      >
        <div className={classNames("mr-2", styles.titleWrap)}>
          <LabelMd weight="semibold" color={styles.titleColor}>
            {title}
          </LabelMd>
        </div>
        <button
          type="button"
          onClick={onClick}
          className={classNames("flex", styles.nameButton)}
          aria-label={ariaLabel}
        >
          <div
            className={classNames(
              "border-dark lg:dark:border-primary border-b border-dashed",
              styles.nameLabelClass,
            )}
          >
            <LabelMd color={styles.nameColor}>{name}</LabelMd>
          </div>
          <div className="shrink-0">
            <Icon
              type="20-chevron-down-sm"
              className={styles.icon}
              aria-hidden
            />
          </div>
        </button>
      </div>
      <div className="shrink-0">
        <button type="button" onClick={handleClose} className={styles.exit}>
          <LabelMd color={styles.exitColor} weight="semibold">
            {t("view_only_masquerading.banner.exit")}
          </LabelMd>
        </button>
      </div>
    </div>
  );
};
